import React from 'react';

/**
 * UncertaintyBand — Trust Infrastructure component.
 *
 * Wraps every numeric output with its uncertainty representation. We refuse
 * to ship a numeric output without a band — the band IS the honesty.
 *
 * Props:
 *   value: central value (number, required)
 *   unit: e.g. 'mm', 't/ha', '°C', 'days', '₹/qtl' (string, optional)
 *   kind: 'plus-minus' | 'iqr' | 'range' | 'class' (required)
 *   For 'plus-minus':  errorPct (number) OR errorAbs (number)
 *   For 'iqr':         lower, upper (numbers)
 *   For 'range':       lower, upper (numbers)
 *   For 'class':       value is the class label string; lower/upper unused
 *   precision: significant digits for value display (default 3)
 *   size: 'sm' | 'md' | 'lg' (default 'md')
 */

function fmt(n, precision = 3) {
  if (typeof n !== 'number' || !isFinite(n)) return String(n ?? '—');
  // Sensible formatting: for integers show no decimals; for sub-unity show 3 sig figs
  const abs = Math.abs(n);
  if (abs >= 100) return n.toFixed(0);
  if (abs >= 10) return n.toFixed(1);
  if (abs >= 1) return n.toFixed(2);
  return n.toPrecision(precision);
}

export default function UncertaintyBand({
  value,
  unit = '',
  kind = 'plus-minus',
  errorPct = null,
  errorAbs = null,
  lower = null,
  upper = null,
  precision = 3,
  size = 'md',
}) {
  const sizeMap = {
    sm: { value: 14, band: 11 },
    md: { value: 18, band: 12.5 },
    lg: { value: 24, band: 13.5 },
  };
  const s = sizeMap[size] || sizeMap.md;

  let bandText = '';
  let valueText = '';

  if (kind === 'class') {
    valueText = String(value);
    bandText = 'class estimate';
  } else if (kind === 'plus-minus') {
    valueText = `${fmt(value, precision)}${unit ? ' ' + unit : ''}`;
    if (errorPct !== null && errorPct !== undefined) {
      bandText = `± ${fmt(errorPct, precision)} %`;
    } else if (errorAbs !== null && errorAbs !== undefined) {
      bandText = `± ${fmt(errorAbs, precision)}${unit ? ' ' + unit : ''}`;
    }
  } else if (kind === 'iqr') {
    valueText = `${fmt(value, precision)}${unit ? ' ' + unit : ''}`;
    bandText = `IQR ${fmt(lower, precision)}–${fmt(upper, precision)}${unit ? ' ' + unit : ''}`;
  } else if (kind === 'range') {
    valueText = `${fmt(value, precision)}${unit ? ' ' + unit : ''}`;
    bandText = `range ${fmt(lower, precision)}–${fmt(upper, precision)}${unit ? ' ' + unit : ''}`;
  }

  return (
    <span
      className="inline-flex items-baseline gap-2"
      data-testid={`uncertainty-band-${kind}`}
    >
      <span
        className="font-display"
        style={{
          fontSize: s.value,
          fontWeight: 500,
          color: '#1B4332',
          letterSpacing: '-0.005em',
          lineHeight: 1.1,
        }}
        data-testid="uncertainty-band-value"
      >
        {valueText}
      </span>
      {bandText && (
        <span
          className="font-body"
          style={{
            fontSize: s.band,
            color: '#6F6A63',
            fontStyle: 'italic',
            fontWeight: 500,
          }}
          data-testid="uncertainty-band-error"
        >
          {bandText}
        </span>
      )}
    </span>
  );
}
